class TradeUpModal extends HTMLElement {
  constructor() {
    super();
    this.openedBefore = false;
    this.allContents = Array.from(this.querySelectorAll('[data-target-variant]'));
    this.closeButtons = Array.from(this.querySelectorAll('[data-close]'));
    this.submitButtons = Array.from(this.querySelectorAll('[name="add-item"]'));
    this.initSubmitButtons();
    this.initCloseButtons();
  }

  static handleOpen(instance, targetVariant) {
    if(!instance.allContents?.length || !targetVariant) return false;
    document.querySelector('html').style.overflow = "hidden";

    const targetContent = instance.allContents.find(content => content.dataset.targetVariant === targetVariant);
    if(!targetContent) return { show: false, action: "swap" };

    // Open modal only if it was not open before
    if(!this.openedBefore) {
      instance.classList.add('open');
      targetContent.classList.remove('hide');
    }
    instance.currentVariantId = parseInt(targetContent.dataset.recommendedVariant);
    instance.action = targetContent.dataset.action;

    const otherContents = instance.allContents.filter(content => content.dataset.targetVariant !== targetVariant);
    if(otherContents.length > 0) {
      otherContents.every(content => content.classList.add('hide'));
    }

    const state = { show: !this.openedBefore ? true : false, action: instance.action }
    this.openedBefore = true;
    return state;
  }

  async getCountryCode() {
    try {
      const response = await fetch("/browsing_context_suggestions.json");
      const responseData = await response.json();
      if(response.ok) {
        if(responseData.detected_values?.country?.handle) {
          return responseData.detected_values.country.handle;
        } else {
          return null;
        }
      }
    } catch(err) {
      console.log(err);
      return null;
    }
  }

  async onSubmitHandler(button) {
    if(this.currentVariantId) {
      if(button.classList.contains('loading')) return;
      if(this.closeButtons?.length > 0) this.closeButtons.forEach(button => button.setAttribute('disabled', 'disabled'));
      button.classList.add('loading');
      button.querySelector('.loading__spinner').classList.remove('hidden');

      const country_code = await this.getCountryCode();
      const urlContainsENCA = window.location.href.includes('/en-ca');

      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: this.currentVariantId,
          quantity: 1
        }),
      })
        .then((response) => response.json())
        .then(async (response) => {
          if (response.status) {
            return;
          }

          // Send added trade up product to analytics
          window.sendProductAddedToCartEvent({
            product: {
              title: response.product_title,
              category: response.product_type,
							upsell_type: 'trade_up'
            },
            variant: {
              id: response.variant_id,
              price: response.price,
              final_price: response.final_price,
            }
          });

          if(this.action === "swap") {
            if (window.plungeAfterTracking) await window.plungeAfterTracking();
            window.location.href = (country_code && country_code === "CA") || urlContainsENCA ? "/en-ca/cart" : "/cart";
          } else {
            const productForm = document.querySelector('product-form form');
            if(!productForm) return;

            const event = new Event('submit', { bubbles: true, cancelable: true });
            productForm.dispatchEvent(event);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          button.classList.remove("loading");
          button.querySelector(".loading__spinner").classList.add("hidden");
        });
    }
  }

  initSubmitButtons() {
    if(this.submitButtons?.length > 0) {
      this.submitButtons.forEach(button => button.addEventListener('click', this.onSubmitHandler.bind(this, button)));
    }
  }

  initCloseButtons() {
    if(!this.closeButtons?.length) return;
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        button.closest('trade-up-modal').classList.remove('open');
        document.querySelector("html").style.overflow = "";
      });
    });
  }
}

if(!customElements.get('trade-up-modal')) {
  customElements.define('trade-up-modal', TradeUpModal);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form.querySelector('[name=id]').disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.addons = document.querySelectorAll('[data-addon]');
    this.showTradeUpModal = { show: false, action: "swap" };
  }

  async getCountryCode() {
    try {
      const response = await fetch("/browsing_context_suggestions.json");
      const responseData = await response.json();
      if(response.ok) {
        if(responseData.detected_values?.country?.handle) {
          return responseData.detected_values.country.handle;
        } else {
          return null;
        }
      }
    } catch(err) {
      console.log(err);
      return null;
    }
  }

  async onSubmitHandler(e) {
    e.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    // The sticky ATC button lives outside the form, linked via the HTML5
    // `form` attribute. Track it so we can show a loading state on whichever
    // button the user actually clicked.
    const stickyButton = document.querySelector('[data-sticky-atc-button]');
    if (submitButton.classList.contains('loading') || stickyButton?.classList.contains('loading')) return;

    submitButton.classList.add('loading');
    this.querySelector('.loading__spinner').classList.remove('hidden');
    stickyButton?.classList.add('loading');
    stickyButton?.querySelector('.loading__spinner')?.classList.remove('hidden');

    const formData = new FormData(this.form);
    const allAddons = this.handleAddons(formData.get("id"));

    // Trade-up modal — bail out before any network if we need to show it
    const tradeUpModal = document.querySelector('trade-up-modal');
    const mainFormVariant = this.form.querySelector('[name=id]').value;
    if(tradeUpModal) {
      this.showTradeUpModal = TradeUpModal.handleOpen(tradeUpModal, mainFormVariant);
    }
    if(this.showTradeUpModal.show && !this.tradeUpWasOpened) {
      this.tradeUpWasOpened = true;
      submitButton.classList.remove('loading');
      this.querySelector('.loading__spinner').classList.add('hidden');
      return;
    }

    // Never bypass included protection through an alternate ATC or programmatic submit.
    if (document.getElementById('plunge-included-protection')) {
      try {
        if (typeof window.plungeAddIncludedProtection !== 'function') throw new Error('Protection is still loading. Please try again.');
        await window.plungeAddIncludedProtection(this.form, formData.get('id'), formData.get('quantity') || 1);
      } catch (error) {
        console.error('Included protection could not be added:', error);
        let message = this.form.querySelector('[data-included-protection-error]');
        if (!message) {
          message = document.createElement('p');
          message.setAttribute('data-included-protection-error', '');
          message.setAttribute('role', 'alert');
          this.form.append(message);
        }
        message.textContent = 'We could not add your included protection. Please try adding to cart again.';
        submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
        stickyButton?.classList.remove('loading');
        stickyButton?.querySelector('.loading__spinner')?.classList.add('hidden');
        return;
      }
      this.form.querySelector('[data-included-protection-error]')?.remove();
    }

    // Fire country-code lookup IN PARALLEL with cart/add — only needed at
    // redirect time. Was previously awaited up front, blocking ~500ms.
    const countryCodePromise = this.getCountryCode();
    const urlContainsENCA = window.location.href.includes('/en-ca');

    // Build the main product line item from form data so we can send it
    // bundled with addons in a single cart/add.js call instead of two
    // serial round trips (was ~3.4s + 3s).
    const mainItem = {
      id: parseInt(formData.get('id')),
      quantity: parseInt(formData.get('quantity') || '1'),
    };
    for (const [key, val] of formData.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m) {
        mainItem.properties = mainItem.properties || {};
        mainItem.properties[m[1]] = val;
      }
    }
    if (formData.get('selling_plan')) {
      mainItem.selling_plan = parseInt(formData.get('selling_plan'));
    }

    const items = [mainItem, ...allAddons.items];

    await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        this.handleErrorMessage(response.description);
        return;
      }

      // Analytics: first response item is the main product, the rest are addons.
      const respItems = response.items || [];
      if (respItems[0]) {
        window.sendProductAddedToCartEvent({
          product: {
            title: respItems[0].product_title,
            category: respItems[0].product_type
          },
          variant: {
            id: respItems[0].variant_id,
            price: respItems[0].price,
            final_price: respItems[0].final_price,
          }
        });
      }
      respItems.slice(1).forEach((item) => {
        window.sendProductAddedToCartEvent({
          product: {
            title: item.product_title,
            category: item.product_type,
            upsell_type: 'add_on'
          },
          variant: {
            id: item.variant_id,
            price: item.price,
            final_price: item.final_price,
          }
        });
      });

      const country_code = await countryCodePromise;
      if (window.plungeAfterTracking) await window.plungeAfterTracking();
      window.location.href = (country_code && country_code === "CA") || urlContainsENCA ? "/en-ca/cart" : "/cart";
    })
    .catch((e) => {
      console.error(e);
    })
    .finally(() => {
      submitButton.classList.remove('loading');
      this.querySelector('.loading__spinner').classList.add('hidden');
      stickyButton?.classList.remove('loading');
      stickyButton?.querySelector('.loading__spinner')?.classList.add('hidden');
    });
  }

  // Returns an object with all the selected addons
  handleAddons(productRef) {
    const addonItems = [];
    if(this.addons?.length > 0) {
      const selectedAddons = Array.from(this.addons).filter(addon => !addon.classList.contains('hidden') && addon.querySelector('input[type="checkbox"]').checked);
      if(selectedAddons?.length > 0) {
        selectedAddons.forEach(addon => {
          const addonDropdown = addon.querySelector('select');
          const selectedOption = addonDropdown.querySelector(`option[value="${addonDropdown.value}"]`);

          // Get the Selling Plan ID and Product ID from option value
          let sellingPlanId;
          let productId;
          if(selectedOption) {
            const valueSplitted = selectedOption.value.split('__');
            if(valueSplitted?.length > 1) {
              productId = parseInt(valueSplitted[1]);
              sellingPlanId = parseInt(valueSplitted[2]);
            }
          }

          // Create addon line item
          const item = {
            quantity: 1,
            properties: {
              "ProductRef": productRef
            }
          };

          if(!sellingPlanId) {
            item['id'] = parseInt(addonDropdown.value)
          } else {
            item['id'] = parseInt(addonDropdown.value.split('__')[0]);
            item['product_id'] = productId;
            item['selling_plan'] = sellingPlanId;
          }

          // Add addon to addonItems
          if(Object.keys(item).length > 2) {
            addonItems.push(item);
          }
        });
      }
    }
    return { items: addonItems };
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define("product-form", ProductForm);
}